'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { useController } from '@lib/hooks/useController';
import { useCallback, useMemo, useState } from 'react';
import { CheckboxWithLabel, InputV2 } from 'ui-components';
import { useEngine } from '../EngineContext';
import { FacetAccordion } from '../FacetAccordion';
import { FacetDropdown } from '../FacetDropdown';
import { getPublisherController } from '../facetControllers';
import { useOptimisticFacetSelection } from '../hooks/useOptimisticFacetSelection';
import { useStableFacetValues } from '../hooks/useStableFacetValues';
import { PublisherFacetProps } from './PublisherFacet.types';

export const PublisherFacet = ({
  variant = 'horizontal',
  className,
  isOpen,
  onToggle,
}: PublisherFacetProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
  }>();

  const publisherLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.publisher.title')
      ?.value ?? 'Publisher';

  const clearLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.publisher.clear')
      ?.value ?? 'Clear';

  const searchPlaceholder =
    searchLabelsMicrocopy?.find(
      (m) => m.key === 'search.publisher.search-placeholder'
    )?.value ?? 'Search publishers...';

  const [searchQuery, setSearchQuery] = useState('');

  const eng = useEngine();
  const controller = useMemo(() => getPublisherController(eng), [eng]);
  const state = useController(controller);
  const stableValues = useStableFacetValues(
    state.values,
    'publisher_name',
    eng
  );
  const { optimisticValues, selectedCount, findStableValue, applyOptimistic } =
    useOptimisticFacetSelection(stableValues);

  const filteredValues = useMemo(() => {
    if (!searchQuery.trim()) return optimisticValues;
    const query = searchQuery.toLowerCase();
    return optimisticValues.filter((v) =>
      v.value.toLowerCase().includes(query)
    );
  }, [optimisticValues, searchQuery]);

  const handleClear = useCallback(() => {
    controller.deselectAll();
  }, [controller]);

  const isDark = variant === 'mobile';

  const content = (
    <div className="flex flex-col">
      <InputV2
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder={searchPlaceholder}
        className="mb-1 w-full text-sm"
        aria-label={searchPlaceholder}
      />
      <div className="max-h-[200px] overflow-y-auto">
        <div className="flex flex-col">
          {filteredValues.map((facetValue) => (
            <CheckboxWithLabel
              key={`publisher-${facetValue.value}`}
              id={`publisher-${facetValue.value}`}
              checked={facetValue.state === 'selected'}
              disabled={
                facetValue.numberOfResults === 0 &&
                facetValue.state !== 'selected'
              }
              onCheckedChange={() => {
                const stableValue = findStableValue(facetValue);
                if (!stableValue) return;
                applyOptimistic([stableValue], () =>
                  controller.toggleSelect(stableValue)
                );
                controller.toggleSelect(stableValue);
              }}
              count={facetValue.numberOfResults}
              size="sm"
              isDarkTheme={isDark}
              microcopy={{ label: facetValue.value }}
            />
          ))}
        </div>
      </div>
    </div>
  );

  if (variant === 'horizontal') {
    return (
      <FacetDropdown
        label={publisherLabel}
        selectedCount={selectedCount}
        clearLabel={clearLabel}
        onClear={handleClear}
        className={className}
      >
        {content}
      </FacetDropdown>
    );
  }

  return (
    <FacetAccordion
      title={publisherLabel}
      variant={variant}
      className={className}
      showClear={variant === 'drawer' && selectedCount > 0}
      onClear={handleClear}
      clearLabel={clearLabel}
      defaultOpen={variant !== 'drawer'}
      selectedCount={selectedCount}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      {content}
    </FacetAccordion>
  );
};
